import { useWindowDimensions, type ViewProps } from "@-/com.npmjs.react-native";
import Animated, { useDerivedValue, useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { LearnMapBottomClip } from "#learn-map-bottom-clip";
import { type LearnMapCardFrame, learnMapCardTravel } from "#learn-map-cards-motion";
import { useLearnMapCoveredOpacityStyle } from "#use-learn-map-covered-opacity-style";
import { useLearnMapModeSelector } from "#use-learn-map-mode";

/** Clips more than the chrome is tall, so it is hidden whole before it is measured. */
const UNMEASURED_FULL_CLIP = 10_000;

/**
 * Chrome between Learn's toolbar and its card (the review recharge bar) that stays put while the
 * card slides up over it on its way to the map. It draws above the card, so it is hidden below the
 * card's top edge as that passes, and uncovered as the card comes back down. It fills the cards'
 * layer, whose coordinates the card's frame is measured in.
 *
 * In landscape the chrome sits beside the card, which never passes over it, so it fades with the
 * cards' backdrop instead.
 */
export function LearnMapCardCovered({ children, ...props }: Omit<ViewProps, "onLayout">) {
  const { isLandscape } = useToolbarGridMetrics();
  const fadeStyle = useLearnMapCoveredOpacityStyle("cards");
  const cardsProgress = useSharedValue(0);
  const unmeasuredCardFrame = useSharedValue<LearnMapCardFrame | null>(null);
  const progress = useLearnMapModeSelector((mode) => mode.progress) ?? cardsProgress;
  const cardFrame = useLearnMapModeSelector((mode) => mode.cardFrame) ?? unmeasuredCardFrame;
  const isMapPresent = useLearnMapModeSelector((mode) => mode.isMapPresent) ?? false;
  const { height: windowHeight } = useWindowDimensions();
  const height = useSharedValue(0);
  const clip = useDerivedValue(() => {
    const frame = cardFrame.get();
    if (isLandscape) {
      return 0;
    }
    if (frame === null) {
      // No card measured yet. Learn opening on its map (a cold launch) mounts this chrome under
      // the open map before the deck lays out its card: it starts hidden, as the card would be.
      return progress.get() >= 1 ? UNMEASURED_FULL_CLIP : 0;
    }
    const cardTop = frame.top - learnMapCardTravel(progress.get(), windowHeight);
    return Math.min(height.get(), Math.max(0, height.get() - cardTop));
  });
  return (
    <LearnMapBottomClip
      {...props}
      clip={clip}
      isClipping={isMapPresent && !isLandscape}
      onLayout={(event) => height.set(event.nativeEvent.layout.height)}
    >
      <Animated.View
        className="size-full"
        pointerEvents="box-none"
        style={isLandscape ? fadeStyle : undefined}
      >
        {children}
      </Animated.View>
    </LearnMapBottomClip>
  );
}
